import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { X } from 'lucide-react';
import axios from 'axios';
import Loader from './Loader';
import { updateUserInfo } from '../features/auth/authSlice';
import { Button, GlassCard } from './ui';

const inputClassName =
  'w-full rounded-btn border border-white/10 bg-bg px-4 py-3 text-sm text-body placeholder:text-muted/70 transition-colors focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/15';

// Helper pour générer les URL d'API
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://website-alturion-production.up.railway.app' 
    : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const BillingForm = ({ show, onHide, onUpdate, user }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [billingName, setBillingName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingCountry, setBillingCountry] = useState('');
  const [billingVAT, setBillingVAT] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      setBillingName(user.billingName || user.name || '');
      setBillingAddress(user.billingAddress || '');
      setBillingZip(user.billingZip || '');
      setBillingCity(user.billingCity || '');
      setBillingCountry(user.billingCountry || '');
      setBillingVAT(user.billingVAT || '');
    }
  }, [user, show]);

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
      };

      const { data } = await axios.put(
        getApiUrl('/api/users/billing'),
        {
          billingName,
          billingAddress,
          billingZip,
          billingCity,
          billingCountry,
          billingVAT,
        },
        config
      );

      const updatedUser = { ...user, ...data };
      
      // Mettre à jour Redux et localStorage
      dispatch(updateUserInfo(updatedUser));
      
      setSuccess(true);
      setLoading(false);
      
      setTimeout(() => {
        if (onUpdate) {
          onUpdate(updatedUser);
        }
      }, 1500);
    } catch (error) {
      setError(
        error.response && error.response.data.message
          ? error.response.data.message
          : t('profile.billingForm.error')
      );
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    onHide();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg/80 backdrop-blur-sm p-4">
      <GlassCard hover={false} className="relative w-full max-w-md mx-auto overflow-hidden">
        <form onSubmit={submitHandler} className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/[.08]">
            <h2 className="text-lg font-semibold text-body">{t('profile.billingForm.title')}</h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-muted hover:text-body transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {error && (
              <div className="mb-4 bg-danger/10 border border-danger/40 text-danger px-4 py-3 rounded-btn">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 bg-success/10 border border-success/40 text-success px-4 py-3 rounded-btn">
                {t('profile.billingForm.success')}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="billingName" className="block text-sm font-medium text-muted mb-1">
                  {t('profile.billingForm.fields.name')}
                </label>
                <input
                  type="text"
                  id="billingName"
                  placeholder={t('profile.billingForm.placeholders.name')}
                  value={billingName}
                  onChange={(e) => setBillingName(e.target.value)}
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="billingAddress" className="block text-sm font-medium text-muted mb-1">
                  {t('profile.billingForm.fields.address')}
                </label>
                <input
                  type="text"
                  id="billingAddress"
                  placeholder={t('profile.billingForm.placeholders.address')}
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="billingZip" className="block text-sm font-medium text-muted mb-1">
                  {t('profile.billingForm.fields.zipCode')}
                </label>
                <input
                  type="text"
                  id="billingZip"
                  placeholder={t('profile.billingForm.placeholders.zipCode')}
                  value={billingZip}
                  onChange={(e) => setBillingZip(e.target.value)}
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="billingCity" className="block text-sm font-medium text-muted mb-1">
                  {t('profile.billingForm.fields.city')}
                </label>
                <input
                  type="text"
                  id="billingCity"
                  placeholder={t('profile.billingForm.placeholders.city')}
                  value={billingCity}
                  onChange={(e) => setBillingCity(e.target.value)}
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="billingCountry" className="block text-sm font-medium text-muted mb-1">
                  {t('profile.billingForm.fields.country')}
                </label>
                <input
                  type="text"
                  id="billingCountry"
                  placeholder={t('profile.billingForm.placeholders.country')}
                  value={billingCountry}
                  onChange={(e) => setBillingCountry(e.target.value)}
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="billingVAT" className="block text-sm font-medium text-muted mb-1">
                  {t('profile.billingForm.fields.vat')}
                </label>
                <input
                  type="text"
                  id="billingVAT"
                  placeholder={t('profile.billingForm.placeholders.vat')}
                  value={billingVAT}
                  onChange={(e) => setBillingVAT(e.target.value)}
                  className={inputClassName}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/[.08] flex flex-col sm:flex-row justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              className="w-full sm:w-auto"
            >
              {t('profile.billingForm.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader size="sm" />
                  <span className="ml-2">{t('profile.billingForm.updating')}</span>
                </span>
              ) : (
                t('profile.billingForm.update')
              )}
            </Button>
          </div>
        </form>
      </GlassCard>
    </div>
  );
};

export default BillingForm;
